"use client";

import React from 'react';

export interface ProblemSolutionCardsBlockProps {
    subtitle?: string;
    title?: string;
    footerTag?: string;
    problemTag?: string;
    problemContent?: string;
    problemFooter?: string;
    solutionTag?: string;
    solutionContent?: string;
    solutionFooterLeft?: string;
    solutionFooterRight?: string;
    style?: {
        backgroundColor?: string;
        textColor?: string;
        accentColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
    };
}

export default function ProblemSolutionCardsBlock({
    subtitle = "01 / OVERVIEW",
    title = "The Challenge \n& Narrative",
    footerTag = "COMPILING DESIGN REQUIREMENTS",
    problemTag = "// THE PROBLEM",
    problemContent = "Hospitality portals often fall into a template-driven trap, bombarding potential guests with aggressive popups, rigid pricing lists, and dry room grids. For Crown Town, this transactional layout failed to convey the premium atmosphere, personal concierge care, and physical serenity that guests experience when stepping into their lobby in Jeddah.",
    problemFooter = "Metric: High bounce rate on generic booking pages",
    solutionTag = "// THE SOLUTION",
    solutionContent = "We designed a luxurious, editorial-inspired interface that prioritizes visual depth and atmospheric breathing room. By dividing the landing architecture into specific segment gateways (Suites, Dining, Wellness, Banquets) and coupling them with a persistent, friction-free booking path, we bridged the gap between immersive brand discovery and direct reservation conversion.",
    solutionFooterLeft = "Core Tech: Next.js + TailwindCSS",
    solutionFooterRight = "Ready & Live",
    style = {}
}: ProblemSolutionCardsBlockProps) {
    const bg = style.backgroundColor || "#09090A";
    const text = style.textColor || "#FAF7F2";
    const accent = style.accentColor || "#c5a880";

    return (
        <section 
            className="px-6 md:px-12 lg:px-24 border-b border-zinc-900/60"
            style={{
                backgroundColor: bg,
                color: text,
                paddingTop: style.paddingTop || "6rem",
                paddingBottom: style.paddingBottom || "6rem"
            }}
        >
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-stretch">
                    
                    {/* Section Tag & Left Column */}
                    <div className="lg:col-span-3 flex flex-col justify-between">
                        <div>
                            {subtitle && (
                                <span className="text-xs font-mono tracking-widest block mb-2" style={{ color: accent }}>
                                    {subtitle}
                                </span>
                            )}
                            {title && (
                                <h2 className="text-3xl font-light text-white font-cormorant whitespace-pre-line leading-tight">
                                    {title}
                                </h2>
                            )}
                        </div>
                        {footerTag && (
                            <div className="hidden lg:block font-mono text-[10px] text-zinc-600 uppercase">
                                {footerTag}
                            </div>
                        )}
                    </div>

                    {/* Problem Card */}
                    <div className="lg:col-span-4 bg-[#0E0E10] border border-zinc-850 rounded-2xl p-8 flex flex-col justify-between space-y-6">
                        <div>
                            {problemTag && (
                                <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: accent }}>
                                    {problemTag}
                                </span>
                            )}
                            {problemContent && (
                                <p className="text-zinc-300 text-sm leading-relaxed font-light mt-4">
                                    {problemContent}
                                </p>
                            )}
                        </div>
                        <div className="h-[1px] bg-zinc-900" />
                        {problemFooter && (
                            <div className="text-[10px] font-mono text-zinc-500">
                                {problemFooter}
                            </div>
                        )}
                    </div>

                    {/* Solution Card */}
                    <div className="lg:col-span-5 bg-[#0E0E10] border border-zinc-850 rounded-2xl p-8 flex flex-col justify-between space-y-6 relative overflow-hidden">
                        <div 
                            className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl pointer-events-none" 
                            style={{ backgroundColor: `${accent}0c` }}
                        />
                        
                        <div>
                            {solutionTag && (
                                <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: accent }}>
                                    {solutionTag}
                                </span>
                            )}
                            {solutionContent && (
                                <p className="text-zinc-300 text-sm leading-relaxed font-light mt-4">
                                    {solutionContent}
                                </p>
                            )}
                        </div>
                        <div className="h-[1px] bg-zinc-900" />
                        {(solutionFooterLeft || solutionFooterRight) && (
                            <div className="text-[10px] font-mono text-zinc-500 flex justify-between items-center">
                                <span>{solutionFooterLeft}</span>
                                <span style={{ color: accent }}>{solutionFooterRight}</span>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </section>
    );
}
